import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTopic,
  deleteTopic,
  fetchTopicById,
  fetchTopics,
  updateTopic,
} from './Topics.services.ts';
import { toast } from 'react-toastify';
import i18n from '../../utils/i18n.ts';
import type { ApiError } from '../../types/App.ts';
import { useMsal } from '@azure/msal-react';
import { orderBy } from 'lodash';

export const useService = ({ topicId }: { topicId: string | null }) => {
  const { accounts } = useMsal();
  const queryClient = useQueryClient();

  const hasAccount = accounts.length > 0;

  const createMutation = useMutation({
    mutationFn: createTopic,
    onSuccess: () => {
      toast.success(i18n.t('success.api.post-item', { item: i18n.t('module.topic') }));
      queryClient.invalidateQueries({ queryKey: ['topics'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateTopic,
    onSuccess: () => {
      toast.success(i18n.t('success.api.put-item', { item: i18n.t('module.topic') }));
      queryClient.invalidateQueries({ queryKey: ['topics'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTopic,
    onSuccess: () => {
      toast.success(i18n.t('success.api.delete-item', { item: i18n.t('module.topic') }));
      queryClient.invalidateQueries({ queryKey: ['topics'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const { data: topic } = useQuery({
    queryKey: ['topic', topicId],
    queryFn: () => fetchTopicById(topicId),
    enabled: !!topicId && hasAccount,
    retry: false,
  });

  const { data: topics, isLoading } = useQuery({
    queryKey: ['topics'],
    queryFn: fetchTopics,
    enabled: hasAccount,
    retry: false,
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    topic,
    topics: orderBy(topics, 'name', 'asc'),
    isLoading,
  };
};